import i18n from "../i18n";
import React, { useState, useEffect } from "react";
import {
  Text,
  Title,
  Button,
  TextInput,
  ActionIcon,
  Group,
  Menu,
  Card,
  Space,
  Grid,
  Center,
  Box,
  UnstyledButton,
  Input,
  Loader,
  Modal,
  Textarea,
  Select,
  Badge,
  Divider,
  Breadcrumbs,
  Anchor,
  Switch,
  ColorInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import {
  IconTrash,
  IconCheck,
  IconSettings,
  IconExclamationMark,
  IconMessageCircle,
  IconCoin,
  IconDownload,
  IconDotsVertical,
  IconList,
  IconGridPattern,
  IconPencil,
  IconLayoutGrid,
  IconRefresh,
  IconFolder,
  IconFolderPlus,
  IconFile,
  IconUpload,
  IconShare,
  IconUsers,
  IconLock,
  IconWorld,
  IconChevronRight,
  IconChevronDown,
  IconHome,
} from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { useAuthUser } from "react-auth-kit";
import FileList from "../components/fileList";
import { Page } from "../layout/page";
import _ from "lodash";

const FileManagers = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const auth = useAuthUser();
  const user = auth();

  //   console.log("Auth user data:", user);
  //   console.log("Auth user ID:", user?._id);

  // State management
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [folderTree, setFolderTree] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  const getFolderId = (folder) => folder?.id || folder?._id;

  const folderToNode = (folder) => ({
    id: getFolderId(folder),
    name: folder.name,
    description: folder.description,
    type: folder.type,
    isPublic: folder.isPublic,
    fileCount: folder.fileCount || 0,
    totalSize: folder.totalSize || 0,
    owner: folder.owner,
    allowUpload: folder.allowUpload,
    allowDelete: folder.allowDelete,
    color: folder.color,
    children: [],
  });

  const buildTreeFromFlat = (flatFolders) => {
    const byId = new Map();
    const roots = [];

    for (const folder of flatFolders) {
      byId.set(String(folder._id), {
        ...folderToNode(folder),
        children: [],
      });
    }

    for (const folder of flatFolders) {
      const node = byId.get(String(folder._id));
      if (folder.parent) {
        const parent = byId.get(String(folder.parent));
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    }

    return roots;
  };

  const addFolderToTree = (tree, folder) => {
    const node = folderToNode(folder);
    const parentId = folder.parent ? String(folder.parent) : null;
    if (!parentId) {
      return [...tree, node];
    }

    const addToParent = (nodes) => {
      for (const n of nodes) {
        if (String(n.id) === parentId) {
          n.children = [...(n.children || []), node];
          return true;
        }
        if (n.children?.length && addToParent(n.children)) {
          return true;
        }
      }
      return false;
    };

    const clone = _.cloneDeep(tree);
    if (!addToParent(clone)) {
      clone.push(node);
    }
    return clone;
  };

  const updateFolderInTree = (tree, folderId, updates) => {
    const updateNodes = (nodes) =>
      nodes.map((node) => {
        if (String(node.id) === String(folderId)) {
          return { ...node, ...updates };
        }
        if (node.children?.length) {
          return { ...node, children: updateNodes(node.children) };
        }
        return node;
      });
    return updateNodes(tree);
  };

  const removeFolderFromTree = (tree, folderId) => {
    const removeNodes = (nodes) =>
      nodes
        .filter((node) => String(node.id) !== String(folderId))
        .map((node) => ({
          ...node,
          children: node.children?.length ? removeNodes(node.children) : [],
        }));
    return removeNodes(tree);
  };

  // Modal states
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [editFolderModal, setEditFolderModal] = useState(false);
  const [shareFolderModal, setShareFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Form states
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDescription, setNewFolderDescription] = useState("");
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [newFolderIsPublic, setNewFolderIsPublic] = useState(true);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderDescription, setEditFolderDescription] = useState("");
  const [editFolderIsPublic, setEditFolderIsPublic] = useState(false);
  const [shareUserId, setShareUserId] = useState("");
  const [sharePermission, setSharePermission] = useState("READ");

  // New states for inline editing
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState("");

  // New states for folder color
  const [editFolderColor, setEditFolderColor] = useState("#4CAF50");

  // Get all available folders for dropdown (including current folder)
  const getAllAvailableFolders = () => {
    const allFolders = [...folders];

    const currentFolderId = getFolderId(currentFolder);
    if (
      currentFolder &&
      !allFolders.find((f) => getFolderId(f) === currentFolderId)
    ) {
      allFolders.push(currentFolder);
    }

    return allFolders;
  };

  // Fetch all accessible folders and build tree
  const fetchFolders = async () => {
    try {
      setTreeLoading(true);
      const result = await api.FileGroup.getAccessibleFolders();

      if (result) {
        setFolders(result);
        const tree = buildTreeFromFlat(result);
        setFolderTree(tree);

        const rootFolderIds = result
          .filter((folder) => !folder.parent)
          .map((folder) => folder._id);
        setExpandedFolders(new Set(rootFolderIds));
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      showNotification({
        title: "Error",
        message: "Failed to load folders",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setTreeLoading(false);
    }
  };

  // Fetch folder details
  const fetchFolderDetails = async (folderId) => {
    const id = getFolderId(folderId);
    if (!id) return;

    try {
      setDetailsLoading(true);
      const folder = await api.getById({
        apiEntity: "fileGroup",
        id,
      });

      if (folder) {
        setCurrentFolder({ ...folder, id: folder._id || folder.id });
        updateBreadcrumbs(folder);
      }
    } catch (error) {
      console.error("Error fetching folder details:", error);
      showNotification({
        title: "Error",
        message: "Failed to load folder details",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  // Update breadcrumbs
  const updateBreadcrumbs = async (folder) => {
    return;
    const breadcrumbs = [];
    let current = folder;

    while (current) {
      breadcrumbs.unshift({
        id: current._id,
        name: current.name,
      });

      if (current.parent) {
        try {
          current = await api.getById({
            apiEntity: "fileGroup",
            id: current.parent,
          });
        } catch (error) {
          break;
        }
      } else {
        break;
      }
    }

    setBreadcrumbs(breadcrumbs);
  };

  // Create new folder
  const handleCreateFolder = async () => {
    try {
      if (!newFolderName.trim()) {
        showNotification({
          title: "Error",
          message: "Folder name is required",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }

      const folderData = {
        name: newFolderName.trim(),
        description: newFolderDescription.trim(),
        parent: newFolderParent,
        type: "FOLDER",
        isPublic: newFolderIsPublic,
        allowUpload: true,
        allowDelete: true,
      };

      const result = await api.add({
        apiEntity: "fileGroup",
        values: folderData,
      });

      if (result && result.success) {
        showNotification({
          title: "Success",
          message: "Folder created successfully",
          color: "green",
          icon: <IconCheck size={18} />,
        });

        setCreateFolderModal(false);
        setNewFolderName("");
        setNewFolderDescription("");
        setNewFolderParent(null);

        const createdFolder = result.data;
        if (createdFolder?._id) {
          setFolders((prev) => [...prev, createdFolder]);
          setFolderTree((prev) => addFolderToTree(prev, createdFolder));
          if (createdFolder.parent) {
            setExpandedFolders(
              (prev) => new Set([...prev, String(createdFolder.parent)])
            );
          }
        } else {
          await fetchFolders();
        }

        if (currentFolder) {
          await fetchFolderDetails(getFolderId(currentFolder));
        }
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      showNotification({
        title: "Error",
        message: "Failed to create folder",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
  };

  // Edit folder
  const handleEditFolder = async () => {
    try {
      console.log("handleEditFolder - selectedFolder:", selectedFolder);
      console.log(
        "handleEditFolder - selectedFolder._id:",
        selectedFolder?._id
      );

      if (!editFolderName.trim() || !selectedFolder) {
        showNotification({
          title: "Error",
          message: "Folder name is required",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }

      const updateData = {
        name: editFolderName.trim(),
        description: editFolderDescription.trim(),
        isPublic: editFolderIsPublic,
      };

      //   console.log("Updating folder:", selectedFolder._id);
      //   console.log("Update data:", updateData);

      const result = await api.updateById({
        apiEntity: "fileGroup",
        id: getFolderId(selectedFolder),
        values: updateData,
      });

      //   console.log("Update result:", result);

      if (result && result.success) {
        showNotification({
          title: "Success",
          message: "Folder updated successfully",
          color: "green",
          icon: <IconCheck size={18} />,
        });

        setEditFolderModal(false);
        setSelectedFolder(null);
        setEditFolderName("");
        setEditFolderDescription("");

        const folderId = getFolderId(selectedFolder);
        setFolderTree((prev) =>
          updateFolderInTree(prev, folderId, {
            name: editFolderName.trim(),
            description: editFolderDescription.trim(),
            isPublic: editFolderIsPublic,
          })
        );
        setFolders((prev) =>
          prev.map((folder) =>
            getFolderId(folder) === folderId
              ? {
                  ...folder,
                  name: editFolderName.trim(),
                  description: editFolderDescription.trim(),
                  isPublic: editFolderIsPublic,
                }
              : folder
          )
        );

        if (currentFolder && getFolderId(currentFolder) === folderId) {
          await fetchFolderDetails(folderId);
        }
      } else {
        console.error("Update failed - no success response:", result);
        showNotification({
          title: "Error",
          message: "Failed to update folder - no success response",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
      }
    } catch (error) {
      console.error("Error updating folder:", error);
      showNotification({
        title: "Error",
        message: "Failed to update folder",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folder) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the folder "${folder.name}"?`
      )
    ) {
      return;
    }

    try {
      const folderId = getFolderId(folder);
      const result = await api.removeById({
        apiEntity: "fileGroup",
        id: folderId,
      });

      if (result && result.success) {
        showNotification({
          title: "Success",
          message: "Folder deleted successfully",
          color: "green",
          icon: <IconCheck size={18} />,
        });

        setFolders((prev) =>
          prev.filter((item) => getFolderId(item) !== folderId)
        );
        setFolderTree((prev) => removeFolderFromTree(prev, folderId));

        if (currentFolder && getFolderId(currentFolder) === folderId) {
          setCurrentFolder(null);
          setBreadcrumbs([]);
        }
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
      showNotification({
        title: "Error",
        message: "Failed to delete folder",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
  };

  // Toggle folder visibility (public/private)
  const handleToggleFolderVisibility = async (folder) => {
    try {
      const folderId = getFolderId(folder);
      const updateData = {
        isPublic: !folder.isPublic,
      };

      const result = await api.updateById({
        apiEntity: "fileGroup",
        id: folderId,
        values: updateData,
      });

      if (result && result.success) {
        showNotification({
          title: "Success",
          message: `Folder is now ${!folder.isPublic ? "public" : "private"}`,
          color: "green",
          icon: <IconCheck size={18} />,
        });

        setFolderTree((prev) =>
          updateFolderInTree(prev, folderId, { isPublic: !folder.isPublic })
        );
        setFolders((prev) =>
          prev.map((item) =>
            getFolderId(item) === folderId
              ? { ...item, isPublic: !folder.isPublic }
              : item
          )
        );

        if (currentFolder && getFolderId(currentFolder) === folderId) {
          await fetchFolderDetails(folderId);
        }
      }
    } catch (error) {
      console.error("Error toggling folder visibility:", error);
      showNotification({
        title: "Error",
        message: "Failed to update folder visibility",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
  };

  // Share folder
  const handleShareFolder = async () => {
    try {
      if (!shareUserId.trim() || !selectedFolder) {
        showNotification({
          title: "Error",
          message: "User ID and permission are required",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }

      const result = await api.search({
        apiEntity: "user",
        pageSize: 1,
        searchQuery: { _id: shareUserId.trim() },
      });

      if (!result || !result.docs || result.docs.length === 0) {
        showNotification({
          title: "Error",
          message: "User not found",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
        return;
      }

      // Call the share endpoint using the API method
      const shareData = await api.FileGroup.share(
        getFolderId(selectedFolder),
        shareUserId.trim(),
        sharePermission
      );

      if (shareData && shareData.success) {
        showNotification({
          title: "Success",
          message: "Folder shared successfully",
          color: "green",
          icon: <IconCheck size={18} />,
        });

        setShareFolderModal(false);
        setSelectedFolder(null);
        setShareUserId("");
        setSharePermission("READ");

        // Refresh current folder if it's the shared one
        if (
          currentFolder &&
          selectedFolder &&
          getFolderId(currentFolder) === getFolderId(selectedFolder)
        ) {
          await fetchFolderDetails(getFolderId(currentFolder));
        }
      }
    } catch (error) {
      console.error("Error sharing folder:", error);
      showNotification({
        title: "Error",
        message: "Failed to share folder",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
  };

  // Toggle folder expansion
  const toggleFolderExpansion = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Select folder
  const selectFolder = (folder) => {
    setCurrentFolder({ ...folder, id: getFolderId(folder) });
    fetchFolderDetails(getFolderId(folder));
    updateBreadcrumbs(folder);
  };

  // Navigate breadcrumb
  const navigateBreadcrumb = (folderId) => {
    if (folderId) {
      fetchFolderDetails(folderId);
    } else {
      setCurrentFolder(null);
      setBreadcrumbs([]);
    }
  };

  // Handle folder name edit
  const handleEditFolderName = async (folder) => {
    const trimmed = editingFolderName.trim();
    if (!trimmed || trimmed === folder.name) {
      setEditingFolderId(null);
      setEditingFolderName("");
      return;
    }
    try {
      const folderId = getFolderId(folder);
      const result = await api.updateById({
        apiEntity: "fileGroup",
        id: folderId,
        values: { name: trimmed },
      });
      if (result && result.success) {
        showNotification({
          title: "Success",
          message: "Folder renamed successfully",
          color: "green",
          icon: <IconCheck size={18} />,
        });
        setFolderTree((prev) =>
          updateFolderInTree(prev, folderId, { name: trimmed })
        );
        setFolders((prev) =>
          prev.map((item) =>
            getFolderId(item) === folderId ? { ...item, name: trimmed } : item
          )
        );
        if (currentFolder && getFolderId(currentFolder) === folderId) {
          setCurrentFolder((prev) => ({ ...prev, name: trimmed }));
        }
      } else {
        showNotification({
          title: "Error",
          message: "Failed to rename folder",
          color: "red",
          icon: <IconExclamationMark size={18} />,
        });
      }
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to rename folder",
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    }
    setEditingFolderId(null);
    setEditingFolderName("");
  };

  // Render folder tree node
  const renderFolderNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isCurrentFolder =
      currentFolder && getFolderId(currentFolder) == node.id;
    const isEditing = editingFolderId === node.id;

    return (
      <Box key={node.id} ml={level * 20}>
        {/* {isCurrentFolder ? "true" : "false"} */}
        <Group
          position="apart"
          p="xs"
          sx={{
            cursor: "pointer",
            borderRadius: 4,
          }}
          onClick={() => selectFolder(node)}
        >
          <Group spacing="xs" sx={{ cursor: "pointer" }}>
            {hasChildren && (
              <ActionIcon
                size="xs"
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderExpansion(node.id);
                }}
              >
                {isExpanded ? (
                  <IconChevronDown size={12} />
                ) : (
                  <IconChevronRight size={12} />
                )}
              </ActionIcon>
            )}
            {!hasChildren && <Box w={20} />}

            <IconFolder size={16} color={node.color || "#4CAF50"} />
            {/* {editingFolderId === node.id ? (
              <TextInput
                size="xs"
                value={editingFolderName}
                autoFocus
                onChange={(e) => setEditingFolderName(e.currentTarget.value)}
                onBlur={() => handleEditFolderName(node)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleEditFolderName(node);
                  } else if (e.key === "Escape") {
                    setEditingFolderId(null);
                    setEditingFolderName("");
                  }
                }}
                sx={{ minWidth: 100, maxWidth: 180 }}
              />
            ) : ( */}
            <Text
              size="sm"
              weight={isCurrentFolder ? 600 : 400}
              //   color={isCurrentFolder ? "blue" : "inherit"}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // setEditingFolderId(node.id);
                // setEditingFolderName(node.name);
                selectFolder(node);
              }}
              sx={{ userSelect: "text", cursor: "pointer" }}
              title="Double click to rename"
            >
              {node.name}
            </Text>
            {/* )} */}

            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={(e) => {
                // e.stopPropagation();
                // handleToggleFolderVisibility({ ...node, _id: node.id });
              }}
              //   title={node.isPublic ? "Make Private" : "Make Public"}
            >
              {!node.isPublic && <IconLock size={12} color="#FF9800" />}
            </ActionIcon>
          </Group>

          <Group spacing={4}>
            {node.fileCount > 0 && (
              <Badge size="xs" variant="light">
                {node.fileCount}
              </Badge>
            )}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical size={12} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  icon={<IconFolderPlus size={14} />}
                  onClick={() => {
                    setNewFolderParent(node.id);
                    setCreateFolderModal(true);
                  }}
                >
                  Create Subfolder
                </Menu.Item>
                <Menu.Item
                  icon={<IconPencil size={14} />}
                  onClick={() => {
                    // console.log("Edit button clicked - node:", node);
                    // console.log("Edit button clicked - node.id:", node.id);
                    // Convert tree node to proper folder object with _id
                    const folderObject = {
                      ...node,
                      _id: node.id,
                    };
                    setSelectedFolder(folderObject);
                    setEditFolderName(node.name);
                    setEditFolderDescription(node.description || "");
                    setEditFolderIsPublic(node.isPublic || false);
                    setEditFolderColor(node.color || "#4CAF50");
                    setEditFolderModal(true);
                  }}
                >{i18n.t("Edit")}</Menu.Item>
                <Menu.Item
                  icon={<IconShare size={14} />}
                  onClick={() => {
                    // Convert tree node to proper folder object with _id
                    const folderObject = {
                      ...node,
                      _id: node.id,
                    };
                    setSelectedFolder(folderObject);
                    setShareFolderModal(true);
                  }}
                >
                  Share
                </Menu.Item>
                <Menu.Item
                  icon={
                    node.isPublic ? (
                      <IconLock size={14} />
                    ) : (
                      <IconWorld size={14} />
                    )
                  }
                  onClick={() => {
                    // Convert tree node to proper folder object with _id
                    const folderObject = {
                      ...node,
                      _id: node.id,
                    };
                    handleToggleFolderVisibility(folderObject);
                  }}
                >
                  Make {node.isPublic ? "Private" : "Public"}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  icon={<IconTrash size={14} />}
                  color="red"
                  onClick={() => {
                    // Convert tree node to proper folder object with _id
                    const folderObject = {
                      ...node,
                      _id: node.id,
                    };
                    handleDeleteFolder(folderObject);
                  }}
                >{i18n.t("Delete")}</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        {hasChildren && isExpanded && (
          <Box>
            {node.children.map((child) => renderFolderNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  // Load data on component mount
  useEffect(() => {
    fetchFolders();
  }, []);

  return (
    <Page title="File Managers">
      <Grid gutter="md" h="calc(100vh - 200px)">
        {/* Left Column - Folder Tree */}
        <Grid.Col span={4}>
          <Card withBorder shadow="sm" h="100%">
            <Group position="apart" mb="md">
              <Title order={5}>{i18n.t("Folders")}</Title>
              <Group spacing="xs">
                <ActionIcon
                  variant="default"
                  onClick={fetchFolders}
                  loading={treeLoading}
                >
                  <IconRefresh size="1rem" />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  onClick={() => setCreateFolderModal(true)}
                >
                  <IconFolderPlus size="1rem" />
                </ActionIcon>
              </Group>
            </Group>

            <Box h="calc(100% - 60px)" style={{ overflowY: "auto" }}>
              {treeLoading ? (
                <Center h="100%">
                  <Loader />
                </Center>
              ) : (
                <Box>
                  {/* Root level */}
                  <UnstyledButton
                    w="100%"
                    p="xs"
                    sx={{
                      cursor: "pointer",
                      borderRadius: 4,
                    }}
                    onClick={() => {
                      setCurrentFolder(null);
                      setBreadcrumbs([]);
                    }}
                  >
                    <Group spacing="xs">
                      <IconHome size={16} />
                      <Text
                        size="sm"
                        weight={!currentFolder ? 600 : 400}
                        color={!currentFolder ? "blue" : "inherit"}
                      >{i18n.t("Home")}</Text>
                    </Group>
                  </UnstyledButton>

                  <Divider my="xs" />

                  {/* Folder tree */}
                  {folderTree.map((node) => renderFolderNode(node))}
                </Box>
              )}
            </Box>
          </Card>
        </Grid.Col>

        {/* Right Column - Files */}
        <Grid.Col span={8}>
          <Card withBorder shadow="sm" h="100%">
            {/* Breadcrumbs */}
            {/* {breadcrumbs.length > 0 && (
              <Breadcrumbs mb="md">
                <Anchor onClick={() => navigateBreadcrumb(null)}>{i18n.t("Home")}</Anchor>
                {breadcrumbs.map((crumb, index) => (
                  <Anchor
                    key={crumb.id}
                    onClick={() => navigateBreadcrumb(crumb.id)}
                  >
                    {crumb.name}
                  </Anchor>
                ))}
              </Breadcrumbs>
            )} */}

            {/* Current folder info */}
            {currentFolder && (
              <Group position="apart" mb="md">
                <Box>
                  <Title order={5}>{currentFolder.name}</Title>
                  {/* <Text size="xs" color="dimmed">
                    {currentFolder.id}
                  </Text> */}
                  {currentFolder.description && (
                    <Text size="sm" color="dimmed">
                      {currentFolder.description}
                    </Text>
                  )}
                  <Group spacing={1} mt="xs">
                    {currentFolder.isPublic ? (
                      <Badge
                        size="sm"
                        color="blue"
                        radius={0}
                        leftSection={<IconWorld size={10} />}
                      >{i18n.t("Public")}</Badge>
                    ) : (
                      <Badge
                        size="sm"
                        color="orange"
                        radius={0}
                        leftSection={<IconLock size={10} />}
                      >
                        Private
                      </Badge>
                    )}
                    <Badge size="sm" variant="light" radius={0}>
                      {currentFolder.fileCount || 0} files
                    </Badge>
                    <Badge size="sm" variant="light" radius={0}>
                      {currentFolder.totalSize
                        ? `${(currentFolder.totalSize / 1024 / 1024).toFixed(
                            1
                          )} MB`
                        : "0 MB"}
                    </Badge>
                  </Group>
                </Box>

                <Group spacing="xs">
                  <ActionIcon
                    variant="default"
                    onClick={() => {
                      setNewFolderParent(getFolderId(currentFolder));
                      setCreateFolderModal(true);
                    }}
                    title="Create Subfolder"
                  >
                    <IconFolderPlus size="1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    onClick={() => handleToggleFolderVisibility(currentFolder)}
                    title={`Make ${
                      currentFolder.isPublic ? "Private" : "Public"
                    }`}
                  >
                    {currentFolder.isPublic ? (
                      <IconLock size="1rem" />
                    ) : (
                      <IconWorld size="1rem" />
                    )}
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    onClick={() => {
                      //   console.log(
                      //     "Edit button clicked - currentFolder:",
                      //     currentFolder
                      //   );
                      //   console.log(
                      //     "Edit button clicked - currentFolder.id:",
                      //     currentFolder?._id
                      //   );
                      setSelectedFolder(currentFolder);
                      setEditFolderName(currentFolder.name);
                      setEditFolderDescription(currentFolder.description || "");
                      setEditFolderIsPublic(currentFolder.isPublic || false);
                      setEditFolderColor(currentFolder.color || "#4CAF50");
                      setEditFolderModal(true);
                    }}
                  >
                    <IconPencil size="1rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    onClick={() => {
                      setSelectedFolder(currentFolder);
                      setShareFolderModal(true);
                    }}
                  >
                    <IconShare size="1rem" />
                  </ActionIcon>
                </Group>
              </Group>
            )}

            {/* File list */}
            <Box h="calc(100% - 120px)" style={{ overflowY: "auto" }}>
              {detailsLoading ? (
                <Center h="100%">
                  <Loader />
                </Center>
              ) : currentFolder ? (
                <FileList
                  relatedObject={{
                    entity: "fileGroup",
                    id: getFolderId(currentFolder),
                    fieldName: "files",
                  }}
                  staticFiles={currentFolder.files}
                  skipInitialFetch={!!currentFolder.files}
                  disabled={!currentFolder.allowUpload}
                  uploadLabel="Upload Files"
                  showScroll={true}
                  height={600}
                />
              ) : (
                <Center h="100%">
                  <Text color="dimmed">{i18n.t("Select a folder to view files")}</Text>
                </Center>
              )}
            </Box>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Create Folder Modal */}
      <Modal
        opened={createFolderModal}
        onClose={() => setCreateFolderModal(false)}
        title={newFolderParent ? "Create Subfolder" : "Create New Folder"}
        size="md"
      >
        <Box>
          <TextInput
            label="Folder Name"
            placeholder="Enter folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.currentTarget.value)}
            mb="md"
            required
          />
          <Textarea
            label="Description"
            placeholder="Enter folder description (optional)"
            value={newFolderDescription}
            onChange={(e) => setNewFolderDescription(e.currentTarget.value)}
            mb="md"
            rows={3}
          />
          <Select
            label="Parent Folder"
            placeholder="Select parent folder (optional)"
            value={newFolderParent}
            onChange={setNewFolderParent}
            data={getAllAvailableFolders().map((f) => ({
              value: f._id,
              label: f.name,
            }))}
            mb="md"
            clearable
          />
          {newFolderParent && (
            <Text size="sm" color="dimmed" mb="md">
              Creating subfolder in:{" "}
              {
                getAllAvailableFolders().find((f) => f._id === newFolderParent)
                  ?.name
              }
            </Text>
          )}
          <Switch
            label="Public Folder"
            description="Make this folder visible to all users"
            checked={newFolderIsPublic}
            onChange={(event) =>
              setNewFolderIsPublic(event.currentTarget.checked)
            }
            mb="md"
          />
          <Group position="right" mt="md">
            <Button
              variant="default"
              onClick={() => {
                setCreateFolderModal(false);
                setNewFolderParent(null);
                setNewFolderIsPublic(false);
              }}
            >{i18n.t("Cancel")}</Button>
            <Button onClick={handleCreateFolder}>{i18n.t("Create Folder")}</Button>
          </Group>
        </Box>
      </Modal>

      {/* Edit Folder Modal */}
      <Modal
        opened={editFolderModal}
        onClose={() => {
          setEditFolderModal(false);
          setEditFolderIsPublic(false);
        }}
        title="Edit Folder"
        size="md"
      >
        <Box>
          <TextInput
            label="Folder Name"
            placeholder="Enter folder name"
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.currentTarget.value)}
            mb="md"
            required
          />
          <Textarea
            label="Description"
            placeholder="Enter folder description (optional)"
            value={editFolderDescription}
            onChange={(e) => setEditFolderDescription(e.currentTarget.value)}
            mb="md"
            rows={3}
          />
          {/* <ColorInput
            label="Folder Icon Color"
            value={editFolderColor}
            onChange={setEditFolderColor}
            mb="md"
          /> */}
          <Switch
            label="Public Folder"
            description="Make this folder visible to all users"
            checked={editFolderIsPublic}
            onChange={(event) =>
              setEditFolderIsPublic(event.currentTarget.checked)
            }
            mb="md"
          />
          <Group position="right" mt="md">
            <Button variant="default" onClick={() => setEditFolderModal(false)}>{i18n.t("Cancel")}</Button>
            <Button onClick={handleEditFolder}>{i18n.t("Update Folder")}</Button>
          </Group>
        </Box>
      </Modal>

      {/* Share Folder Modal */}
      <Modal
        opened={shareFolderModal}
        onClose={() => setShareFolderModal(false)}
        title="Share Folder"
        size="md"
      >
        <Box>
          <TextInput
            label="User ID"
            placeholder="Enter user ID to share with"
            value={shareUserId}
            onChange={(e) => setShareUserId(e.currentTarget.value)}
            mb="md"
            required
          />
          <Select
            label="Permission"
            placeholder="Select permission level"
            value={sharePermission}
            onChange={setSharePermission}
            data={[
              { value: "READ", label: "Read Only" },
              { value: "WRITE", label: "Read & Write" },
              { value: "ADMIN", label: "Admin" },
            ]}
            mb="md"
          />
          <Group position="right" mt="md">
            <Button
              variant="default"
              onClick={() => setShareFolderModal(false)}
            >{i18n.t("Cancel")}</Button>
            <Button onClick={handleShareFolder}>{i18n.t("Share Folder")}</Button>
          </Group>
        </Box>
      </Modal>
    </Page>
  );
};

export default FileManagers;
